"use client";

import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Sticky footer action row — Radix keeps focus trapped inside the drawer. */
  footer?: ReactNode;
  className?: string;
}

/**
 * Generic right-side contextual workspace shell (Reservation Drawer's
 * structural parent). This is intentionally content-agnostic — Phase 0 ships
 * no booking/reservation logic — so the body is a plain slot. It is demoed
 * in /design-system with guest-header-shaped placeholder content.
 *
 * Built on Radix Dialog for accessible focus trapping, Escape-to-close,
 * scroll locking and portal rendering — the "accessible headless primitive"
 * chosen for Phase 0 (see final report for rationale).
 */
export function Drawer({ open, onOpenChange, title, description, children, footer, className }: DrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay fixed inset-0 z-40 bg-[rgba(23,25,21,0.4)]" />
        <Dialog.Content
          className={cn(
            "drawer-content fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col border-s border-border-subtle bg-surface-elevated shadow-[var(--shadow-lifted)]",
            className,
          )}
        >
          <header className="flex items-start justify-between gap-3 border-b border-border-subtle p-5">
            <div className="min-w-0">
              <Dialog.Title className="font-display truncate text-xl font-medium text-text-primary">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-xs text-text-secondary">{description}</Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close panel"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control border border-border-subtle bg-surface-primary text-text-secondary hover:bg-surface-secondary"
              >
                <X aria-hidden="true" size={16} />
              </button>
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto p-5">{children}</div>

          {footer ? (
            <footer className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-border-subtle bg-surface-elevated p-4">
              {footer}
            </footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
